import { Group, ListViewGroup, ListViewRow, Text } from '@ekhodealer/ekho-common/rev';

import type { EkhoFinancingStipKey } from '@ekhodealer/ekho-common';

import { DetailedInfoLineItem } from '@common/components/DetailedInfoLineItem';
import CheckCircleFilled16 from '@common/components/icons/CheckCircleFilled16';
import XCircleFilled16 from '@common/components/icons/XCircleFilled16';
import { formatCurrency } from '@common/lib/currency';

import PendingCircleFilled16 from './icons/PendingCircleFilled16';

export interface LenderDecision {
	lenderName: string;
	apr?: number;
	termMonths?: number;
	additionalDown?: number;
	status?: string;
	notes?: string;
}

export interface Submission {
	apr?: number;
	termMonths?: number;
	additionalDown?: number;
	status?: string;
	lenderName: string;
	stipulations?: {
		[key: string]: {
			ekhoStipKey: EkhoFinancingStipKey;
			validated: boolean;
			index: number;
			dateCreated: number;
			dateValidated?: number;
		};
	};
}

interface LenderDecisionsListProps {
	lenderDecisions: LenderDecision[];
}

const buildSubmissionTermsString = (submission: Submission): string => {
	const parts: string[] = [];

	if (submission.apr !== undefined) {
		parts.push(`${(submission.apr * 100).toFixed(2)}% APR`);
	}

	if (submission.termMonths !== undefined) {
		parts.push(`${submission.termMonths}-month term`);
	}

	if (submission.additionalDown !== undefined) {
		parts.push(`${formatCurrency(submission.additionalDown)} additional down`);
	}

	return parts.join(' • ');
};

const getSubmissionStatusIcon = (status?: string): React.ReactElement | null => {
	if (!status) {
		return null;
	}

	if (status === 'Approved') {
		return <CheckCircleFilled16 />;
	}
	if (status === 'Conditionally Approved') {
		return <PendingCircleFilled16 />;
	}
	if (status === 'Rejected') {
		return <XCircleFilled16 />;
	}
	return null;
};

const renderSubmissionStatus = (submission: Submission): React.ReactElement => {
	const { status, stipulations } = submission;
	let finalStatus = status;
	if (
		status === 'Approved' &&
		stipulations &&
		Object.values(stipulations).some((stip) => !stip.validated)
	) {
		finalStatus = 'Conditionally Approved';
	}
	return (
		<Group>
			<Text size='footnote'>{finalStatus}</Text>
			{getSubmissionStatusIcon(finalStatus)}
		</Group>
	);
};

const LenderDecisionDetailInfoItem = ({ submission }: { submission: Submission }) => (
	<DetailedInfoLineItem
		primaryLabel={submission.lenderName}
		secondaryLabel={buildSubmissionTermsString(submission)}
		rightAccessory={renderSubmissionStatus(submission)}
	/>
);

/**
 * Renders a list of lender decisions in a ListView format
 * Used for displaying financing decisions across orders and leads
 */
export const LenderDecisionsList = ({ lenderDecisions }: LenderDecisionsListProps) => {
	if (lenderDecisions.length === 0) {
		return null;
	}

	return (
		<ListViewGroup aria-label='Lender decisions'>
			{lenderDecisions.map((submission: LenderDecision, index: number) => (
				<ListViewRow
					key={index}
					bottomContent={<LenderDecisionDetailInfoItem submission={submission} />}
				/>
			))}
		</ListViewGroup>
	);
};
